/**
 * BowerSpinner: the brand loading mark. One mark at every size, from a 12px
 * table cell to a page-level wait.
 *
 * WHAT IT IS. A ~255° arc stroked in the logo mark's ramp, rotating. The
 * gradient is oriented so all three stops land on the arc: bluebird at the
 * top-left, twilight through the middle, the head (Sunset: 500 in light, 400 in
 * dark) at the front, fading back toward the tail. It replaces Chakra's
 * `<Spinner>`, which is a bordered div
 * with two sides set transparent and carries no identity at all.
 *
 * INLINE WAITS ONLY, since 2026-09-25. A logo-shaped page mark was built and
 * rejected on 2026-09-24; a different one, `BowerNestLoader` (the mark's own
 * feathers weaving into a nest), was approved by David the next day for any
 * wait that OWNS a page, pane or tab. This one stays the mark for everything
 * smaller: buttons, rows, status lines, dialogs. Content with a known shape
 * keeps its skeleton. Within this component, what changes with size is the
 * speed, not the shape. See SIZES below.
 *
 * TWO TONES, because the app already made that distinction and it was worth
 * keeping. Most spinners are the brand comet. A handful are deliberately quiet
 * inline ones sitting next to secondary text, and those take `tone="muted"`,
 * which drops the gradient for flat `currentColor` so the call site's own
 * colour governs. See the `tone` prop.
 *
 * THE TAIL DOES NOT REACH ZERO, and that is the one deliberate difference from
 * a textbook comet. At `stop-opacity: 0` the faint end goes sub-pixel at 12-16px
 * and the mark stops reading as a ring at exactly the size it is used most.
 * The 0.18 floor is invisible at 48px and is what holds the circle together at
 * 12px.
 *
 * WHY THE GAP IS NOT ROTATED ONTO THE FADE. Turning the arc so the transparent
 * end of the gradient sits exactly on the dash's tail makes a cleaner comet and
 * a worse mark: it walks the arc off the head's corner of the gradient box and
 * the head stop disappears entirely. Tried, rejected, do not re-derive it.
 *
 * PAINT. The stroke references a document-scoped <linearGradient> rendered once
 * by `<BrandIconGradientDefs />`, the same way gradient BowerIcons do, so light
 * and dark follow `spinner.gradientStop1..3` with no JS and no second palette.
 * In light mode the tail and mid are bluebird and twilight at the 700 step: at
 * the icon step the arc measured 1.43:1 and failed WCAG 1.4.11, because an icon
 * is a shape beside a label while a spinner is often the only thing on screen
 * saying anything is happening. The head is Sunset 500, the brand colour, under
 * 3:1 (about 1.7 on the page grounds, 1.43 on `bg.subtle`), a trade recorded in
 * `spinnerContrast.test.ts` (David, 2026-10-17). Dark mode is identical to the
 * icon ramp. The
 * trailing `currentColor` in the paint value is a FALLBACK, not decoration: a
 * `url(#…)` whose target is missing is treated as `none`, so without it a
 * spinner rendered in a second React root or a bare test harness is an empty
 * box that throws nothing. See `theme/iconGradient.ts` for the long version.
 *
 * THREE ENVIRONMENTS STRIP THE GRADIENT and each one is handled below, because
 * in all three the failure mode is "the person waiting sees nothing":
 *
 *   forced-colors   the UA may not resolve a paint server; fall back to
 *                   `currentColor`, which it HAS resolved for this context.
 *   print           backgrounds and paint servers are commonly dropped; fall
 *                   back to the mid stop as a flat ink.
 *   reduced-motion  the rotation stops, the arc STAYS DRAWN. Hiding it would
 *                   remove the only sign that anything is happening.
 */

import { Box, type BoxProps } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

import { SPINNER_FLAT_FALLBACK, SPINNER_GRADIENT_SVG_ID } from '../../theme/iconGradient';

const rotate = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * Diameter and revolution time per size.
 *
 * The diameters are Chakra's `Spinner` scale to the pixel (xs `sizes.3`, sm
 * `sizes.4`, md `sizes.6`, lg `sizes.8`, xl `sizes.12`), so swapping a call
 * site over cannot shift a layout. `2xl` is an addition above Chakra's scale.
 * A wait that fills a page uses `BowerNestLoader` instead.
 *
 * SPEED FALLS AS SIZE RISES, which is the part that is easy to leave out. A
 * mark's edge travels π·d per revolution, so holding one duration across the
 * scale makes the 72px spinner's edge move six times faster than the 16px
 * one's and it reads as panic on a screen someone is waiting three minutes in
 * front of. These numbers keep the small ones snappy and let the big ones
 * settle, without the big ones crawling.
 */
const SIZES = {
  xs: { px: '0.75rem', duration: '0.85s' },
  sm: { px: '1rem', duration: '0.95s' },
  md: { px: '1.5rem', duration: '1.1s' },
  lg: { px: '2rem', duration: '1.25s' },
  xl: { px: '3rem', duration: '1.45s' },
  '2xl': { px: '4.5rem', duration: '1.7s' },
} as const;

export type BowerSpinnerSize = keyof typeof SIZES;

/**
 * Geometry, in the 24-unit viewBox.
 *
 * `DASH` is the drawn length and the gap, against a circumference of 2π·9 ≈
 * 56.5: 35 units of arc is a shade over 255°, which leaves a gap wide enough
 * to read as motion at 12px without the mark looking broken.
 */
const RADIUS = 9;
const STROKE = 3;
const DASH = '35 22';

const PAINT = `url(#${SPINNER_GRADIENT_SVG_ID}) currentColor`;

export interface BowerSpinnerProps extends Omit<BoxProps, 'children'> {
  /** @default 'md' */
  size?: BowerSpinnerSize;
  /**
   * `brand` paints the comet in the icon gradient and IGNORES any `color` prop,
   * because a paint server beats `currentColor`. `muted` strokes flat
   * `currentColor` instead, so `color` governs.
   *
   * Reach for `muted` only where the app already chose to be quiet: a small
   * spinner beside secondary text, inside a row that is not the subject of the
   * screen. Everywhere else the gradient IS the point.
   *
   * This exists so that a `color` prop is never silently inert. Under `brand` a
   * colour is ignored, which is invisible in review and looks deliberate in the
   * diff, so those call sites either drop the colour or say `tone="muted"`.
   *
   * @default 'brand'
   */
  tone?: 'brand' | 'muted';
  /**
   * What a screen reader announces.
   *
   * Pass `null` when the spinner sits beside visible text that already says
   * what is happening: a status cell reading "Importing", a button whose
   * label changed. Forty table rows each announcing "Loading" is noise, and
   * the row's own text is the better announcement.
   *
   * @default 'Loading'
   */
  label?: string | null;
}

export function BowerSpinner({
  size = 'md',
  tone = 'brand',
  label = 'Loading',
  ...rest
}: BowerSpinnerProps) {
  const { px, duration } = SIZES[size];
  const decorative = label === null;

  return (
    <Box
      as="svg"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      flexShrink={0}
      display="block"
      // `img`, deliberately NOT `status`.
      //
      // A spinner is a picture of a state, not the announcement of one. Making
      // it a live region sounds more accessible and is not: the page usually
      // ALREADY has a region that owns the message ("Converting document…"),
      // so a second one announces the same change twice, and a table rendering
      // forty rows would open forty live regions. Chakra's Spinner never did
      // this either, so keeping it out is also what makes this swap behaviour
      // preserving across ninety call sites.
      //
      // Announcing that work STARTED belongs to whatever container changed.
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      sx={{
        animation: `${rotate} ${duration} linear infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
      {...rest}
    >
      <Box
        as="circle"
        cx={12}
        cy={12}
        r={RADIUS}
        fill="none"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={DASH}
        sx={
          tone === 'muted'
            ? { stroke: 'currentColor' }
            : {
                stroke: PAINT,
                '@media (forced-colors: active)': { stroke: 'currentColor' },
                '@media print': { stroke: SPINNER_FLAT_FALLBACK },
              }
        }
      />
    </Box>
  );
}
